import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api } from '../api.js'
import { useAuth } from '../auth/AuthContext.jsx'

// Settings → Participants. List only — add/edit live on their own pages
// (/settings/participants/new, /settings/participants/:id/edit).
// Participants are NEVER deleted: deactivate/reactivate only. Inactive
// rows stay listed with a badge and can't be enrolled until reactivated.

export function ParticipantsPage() {
  const { t } = useTranslation()
  const { user, loading: authLoading } = useAuth()

  const [participants, setParticipants] = useState([])
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState(null)
  const [error, setError] = useState(null)

  function reload() {
    setLoading(true)
    api.participants.list()
      .then((d) => setParticipants(d.participants || []))
      .catch((e) => setError(e.code || 'server'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (authLoading || !user) return
    reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user])

  async function toggle(p) {
    const inactive = !!p.deactivated_at
    if (!inactive && !confirm(t('participants.confirm_deactivate', { name: p.name }))) return
    setBusyId(p.id); setError(null)
    try {
      const call = inactive ? api.participants.reactivate : api.participants.deactivate
      const { participant } = await call(p.id)
      setParticipants((list) => list.map((x) => (x.id === participant.id ? participant : x)))
    } catch (e) {
      setError(e.code || 'server')
    } finally { setBusyId(null) }
  }

  if (authLoading) return <main className="container"><p>{t('common.loading')}</p></main>
  if (!user) return <main className="container"><p className="error">{t('auth.errors.not_authenticated')}</p></main>

  return (
    <main className="container">
      <section className="card">
        <div className="page-head">
          <h1>{t('participants.title')}</h1>
          <Link to="/settings/participants/new"><button type="button">+ {t('participants.add')}</button></Link>
        </div>

        <p className="muted">{t('participants.intro')}</p>

        {error && (
          <p className="error">
            {t(`participants.errors.${error}`, { defaultValue: t(`auth.errors.${error}`, { defaultValue: error }) })}
          </p>
        )}

        {loading && <p>{t('common.loading')}</p>}

        {!loading && participants.length === 0 && (
          <p className="muted">{t('participants.empty_state')}</p>
        )}

        {!loading && participants.length > 0 && (
          <ul className="participants-list">
            {participants.map((p) => {
              const inactive = !!p.deactivated_at
              return (
                <li key={p.id} className="participant-row">
                  <div className="participant-main" style={inactive ? { opacity: 0.55 } : undefined}>
                    <strong>{p.name}</strong>
                    {Number(p.is_self) === 1 ? <span className="badge">{t('participants.self_badge')}</span> : null}
                    {inactive ? <span className="badge muted-badge">{t('participants.inactive_badge')}</span> : null}
                    <div className="participant-meta">
                      {p.birthdate && <span>{p.birthdate}</span>}
                      {p.phone && <span>{p.phone}</span>}
                      {p.email && <span>{p.email}</span>}
                    </div>
                  </div>
                  <div className="participant-actions">
                    <Link to={`/settings/participants/${p.id}/edit`} className="link-button">
                      {t('common.edit')}
                    </Link>
                    <button
                      type="button"
                      className={`link-button${inactive ? '' : ' danger'}`}
                      disabled={busyId === p.id}
                      onClick={() => toggle(p)}
                    >
                      {inactive ? t('participants.reactivate') : t('participants.deactivate')}
                    </button>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </section>
    </main>
  )
}
